import { useState, useEffect, useMemo } from "react";
import JSZip from "jszip";
import { parseMigdalZip, normPolNum } from "../parsers/migdalParser";
import { parseXMLFiles } from "../parsers/xmlParser";
import { useClientStore } from "../hooks/useClientStore";
import { useHoldingsStore } from "../hooks/useHoldingsStore";
import { SOURCE_TYPE } from "../utils/dataSource";
import { deriveValueMonth, monthToValueDate } from "../utils/valueMonth";

// CSS styles inline for simplicity (matching HachsharaImport)
const S = {
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(15,23,42,0.7)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999, padding: 20, direction: "rtl", fontFamily: "system-ui, sans-serif"
  },
  modal: {
    background: "white", borderRadius: 24, width: "100%", maxWidth: 800,
    maxHeight: "90vh", display: "flex", flexDirection: "column",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", overflow: "hidden"
  },
  header: {
    padding: "24px 32px", background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    color: "white", display: "flex", justifyContent: "space-between", alignItems: "center"
  },
  content: {
    padding: 32, overflowY: "auto", flex: 1, background: "#f8fafc"
  },
  footer: {
    padding: "20px 32px", background: "white", borderTop: "1px solid #e2e8f0",
    display: "flex", justifyContent: "flex-end", gap: 12
  },
  card: {
    background: "white", borderRadius: 16, padding: 20, marginBottom: 20,
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0"
  },
  btn: {
    padding: "10px 24px", borderRadius: 12, fontWeight: 600, fontSize: 15,
    cursor: "pointer", transition: "all 0.2s", border: "none"
  },
  btnPrimary: { background: "#3b82f6", color: "white", boxShadow: "0 4px 12px rgba(59,130,246,0.3)" },
  btnSecondary: { background: "#f1f5f9", color: "#475569" },
  input: {
    padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1",
    fontSize: 14, width: "100%", outline: "none"
  }
};

export default function MigdalImport({ files, existingClients, onClose }) {
  const { addHoldingsFunds } = useHoldingsStore();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importData, setImportData] = useState(null);
  
  // State for manual names for unmatched members
  const [newNames, setNewNames] = useState({});
  // חודש-ערך חובה — נבחר/מאושר ידנית לפני שמירה (מקור אמת יחיד לתאריך הנכונות)
  const [valueMonth, setValueMonth] = useState("");

  useEffect(() => {
    async function process() {
      try {
        setLoading(true);
        const file = files[0];
        const zip = await JSZip.loadAsync(file);

        const zipFiles = await Promise.all(
          Object.values(zip.files).filter(f => !f.dir).map(async entry => ({
            name: entry.name,
            content: await entry.async("arraybuffer")
          }))
        );

        // ── 1. הקובץ האחיד (DAT/XML, SUG-MIMSHAK=1) — המקור הסמכותי ─────────
        // מפוענח דרך אותו parseXMLFiles של זרימת האחזקות הרגילה.
        const xmlEntries = zipFiles.filter(f => /\.(dat|xml)$/i.test(f.name));
        let dat = { funds: [], taarichBitzua: null, clientsCount: 0, shemYatzran: null };
        if (xmlEntries.length) {
          const xmlContents = xmlEntries.map(entry => {
            const sniff = new TextDecoder("utf-8").decode(new Uint8Array(entry.content, 0, 200));
            const encMatch = sniff.match(/encoding=["']([^"']+)["']/i);
            const enc = encMatch ? encMatch[1] : "utf-8";
            return { name: entry.name, content: new TextDecoder(enc).decode(entry.content) };
          });
          const { funds, taarichBitzua } = parseXMLFiles(xmlContents);
          dat = {
            funds: funds || [],
            taarichBitzua,
            clientsCount: new Set((funds || []).map(f => f.client?.id).filter(Boolean)).size,
            shemYatzran: funds?.[0]?.companyName || null,
          };
        }
        const datPolSet = new Set(dat.funds.map(f => normPolNum(f.polNum)).filter(Boolean));

        // ── 2. קבצי MBT (פרט/בריאות) — רק פוליסות שאינן כבר בקובץ האחיד ────
        const parsed = parseMigdalZip(file.name, zipFiles);
        const members = parsed.members
          .map(m => ({ ...m, funds: m.funds.filter(f => !datPolSet.has(f.normPolNum)) }))
          .filter(m => m.funds.length > 0);

        if (!dat.funds.length && !members.length) {
          throw new Error("לא נמצאו פוליסות בקובץ — לא בקובץ האחיד (DAT) ולא בקבצי ה-MBT.");
        }

        // Match against existing clients by TAZ
        const matched = [];
        const unmatched = [];

        members.forEach(m => {
          // Normalize TAZ (remove leading zeros for comparison if needed, or keep 9 digits)
          const searchTaz = m.taz ? String(Number(m.taz)) : null;
          const found = existingClients.find(c =>
            c.id === m.taz ||
            c.clientId === m.taz ||
            (c.clientId && String(Number(c.clientId)) === searchTaz) ||
            (c.id && String(Number(c.id)) === searchTaz)
          );

          if (found) {
            matched.push({ ...m, clientId: found.id, existingName: found.clientName || found.name });
          } else {
            unmatched.push({ ...m, clientId: null });
          }
        });

        setImportData({ ...parsed, matched, unmatched, dat });
        setValueMonth(deriveValueMonth(file.name, dat.taarichBitzua || parsed.reportDate));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    process();
  }, [files, existingClients]);

  const handleConfirm = () => {
    if (!importData) return;
    if (!valueMonth) return; // חודש-ערך חובה

    let createdCount = 0;
    const flatFunds = [];

    // f = פוליסה שלמה מהפרסר (כולל kisuimMigdal, insuredName, premium, premiumsPaid)
    const toFund = (f, client) => ({
      ...f,
      id: f.id || `10_${f.polNum || ""}`,
      cat: "פרט",
      icon: "🏢",
      sugMutzar: "פרט",
      tochnit: f.name,
      statusLabel: f.statusLabel || "פעיל",
      client,
    });

    // Process matched
    importData.matched.forEach(m => {
      m.funds.forEach(f => flatFunds.push(toFund(f, { id: m.clientId, name: m.existingName })));
    });

    // Process unmatched (create new clients)
    importData.unmatched.forEach(m => {
      let finalName = newNames[m.taz] || `${m.firstName || ""} ${m.lastName || ""}`.trim();
      if (!finalName) finalName = "לקוח חדש (מגדל)";
      if (m.taz) createdCount++;
      m.funds.forEach(f => flatFunds.push(toFund(f, { id: m.taz, name: finalName })));
    });

    const fileMeta = {
      fileName: files[0].name,
      taarichBitzua: monthToValueDate(valueMonth),
      companyCode: "10",
      shemYatzran: importData.dat?.shemYatzran || "מגדל",
      sourceType: SOURCE_TYPE.PRAT,
    };

    // 1. הקובץ האחיד — קרנות מבנה-אחיד מלאות (מנהלים/גמל), עם client מתוך ה-XML עצמו.
    let datClients = 0;
    if (importData.dat?.funds?.length) {
      const res = addHoldingsFunds(importData.dat.funds, fileMeta);
      datClients = res.clientsTouched;
    }

    // 2. פוליסות פרט/בריאות מה-MBT (לאחר סינון כפילויות מול ה-DAT).
    if (flatFunds.length) addHoldingsFunds(flatFunds, fileMeta);

    const mbtClients = importData.matched.length + createdCount;
    alert(`יובאו בהצלחה: ${importData.dat?.funds?.length || 0} פוליסות מהקובץ האחיד (${datClients} לקוחות) + ${flatFunds.length} פוליסות פרט/בריאות (${mbtClients} לקוחות).`);
    onClose();
  };

  if (loading) {
    return (
      <div style={S.overlay}>
        <div style={{...S.modal, width: 400, padding: 40, alignItems: "center"}}>
          <div style={{ fontSize: 40, marginBottom: 20, animation: "spin 1s linear infinite" }}>🔄</div>
          <h3 style={{ margin: 0 }}>קורא קובץ מגדל...</h3>
          <p style={{ color: "#64748b", marginTop: 8 }}>מפענח כיסויי בריאות ופרט מקובץ ה-ZIP</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={S.overlay}>
        <div style={{...S.modal, width: 400, padding: 32, alignItems: "center"}}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>❌</div>
          <h3 style={{ margin: "0 0 16px 0", color: "#ef4444" }}>שגיאה בפענוח הקובץ</h3>
          <p style={{ color: "#64748b", textAlign: "left", marginBottom: 24, whiteSpace: "pre-wrap", maxHeight: 300, overflowY: "auto", background: "#f8fafc", padding: 12, borderRadius: 8, fontSize: 12, border: "1px solid #e2e8f0" }}>{error}</p>
          <button style={{...S.btn, ...S.btnSecondary}} onClick={onClose}>סגור</button>
        </div>
      </div>
    );
  }

  const { matched, unmatched, dat } = importData;

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.header}>
          <div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>ייבוא מגדל (פרט ובריאות)</h2>
            <div style={{ fontSize: 14, opacity: 0.9, marginTop: 4 }}>
              קובץ: {files[0].name}
            </div>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 13, fontWeight: 700 }}>
              📅 חודש הערך (נכונות){!valueMonth && <span style={{ color: "#fecaca" }}> — חובה</span>}:
              <input
                type="month"
                value={valueMonth}
                onChange={e => setValueMonth(e.target.value)}
                style={{
                  padding: "4px 8px", borderRadius: 6, border: "none",
                  fontSize: 13, fontWeight: 700, color: "#1e3a8a",
                }}
              />
            </label>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"white", fontSize: 28, cursor:"pointer" }}>×</button>
        </div>

        <div style={S.content}>
          {dat?.funds?.length > 0 && (
            <div style={{ ...S.card, background: "#eff6ff", border: "1px solid #bfdbfe" }}>
              <h3 style={{ margin: "0 0 8px 0", color: "#1e3a8a", display: "flex", alignItems: "center", gap: 8 }}>
                📦 קובץ אחיד (מבנה 008) — {dat.shemYatzran || "מגדל"}
              </h3>
              <div style={{ fontSize: 14, color: "#334155" }}>
                <strong>{dat.funds.length}</strong> פוליסות מנהלים/גמל · <strong>{dat.clientsCount}</strong> לקוחות
                {dat.taarichBitzua && <> · תאריך ערך: {dat.taarichBitzua}</>}
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
                ייקלטו אוטומטית עם כל השדות התקניים (שמות מתוך הקובץ). פוליסות פרט/בריאות שכבר מופיעות כאן סוננו מהרשימות למטה למניעת כפילות.
              </div>
            </div>
          )}

          <div style={S.card}>
            <h3 style={{ margin: "0 0 16px 0", color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
              ✅ לקוחות קיימים שזוהו ({matched.length})
            </h3>
            {matched.length === 0 ? (
              <div style={{ color: "#94a3b8", fontSize: 14 }}>לא נמצאו לקוחות קיימים.</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 12 }}>
                {matched.map(m => (
                  <div key={m.taz} style={{ background: "#f8fafc", padding: 12, borderRadius: 12, border: "1px solid #e2e8f0" }}>
                    <div style={{ fontWeight: 700, color: "#1e3a8a" }}>{m.existingName}</div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>ת"ז: {m.taz}</div>
                    <div style={{ fontSize: 12, color: "#059669", marginTop: 4, fontWeight: 600 }}>
                      {m.funds.length} פוליסות ({m.funds.reduce((s, f) => s + (f.kisuimMigdal?.length || 0), 0)} כיסויים)
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.card}>
            <h3 style={{ margin: "0 0 16px 0", color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
              ✨ לקוחות חדשים ({unmatched.length})
            </h3>
            {unmatched.length === 0 ? (
              <div style={{ color: "#94a3b8", fontSize: 14 }}>אין לקוחות חדשים.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {unmatched.map(m => {
                  const suggested = `${m.firstName || ""} ${m.lastName || ""}`.trim();
                  return (
                    <div key={m.taz} style={{ background: "#fffbeb", padding: 16, borderRadius: 12, border: "1px solid #fde68a", display: "flex", gap: 16, alignItems: "center" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: "#92400e", fontWeight: 600, marginBottom: 4 }}>
                          ת"ז: {m.taz} | {m.funds.length} פוליסות ({m.funds.reduce((s, f) => s + (f.kisuimMigdal?.length || 0), 0)} כיסויים)
                          {m.firstName ? ` | ${m.firstName} ${m.lastName || ""}` : ""}
                        </div>
                        <input 
                          style={S.input}
                          placeholder="הזן שם לקוח..."
                          value={newNames[m.taz] ?? suggested}
                          onChange={e => setNewNames({...newNames, [m.taz]: e.target.value})}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={S.footer}>
          <button style={{...S.btn, ...S.btnSecondary}} onClick={onClose}>ביטול</button>
          <button
            style={{ ...S.btn, ...(valueMonth ? S.btnPrimary : { background: "#cbd5e1", color: "white", cursor: "not-allowed" }) }}
            onClick={handleConfirm}
            disabled={!valueMonth}
            title={valueMonth ? undefined : "יש לבחור חודש ערך לפני שמירה"}
          >
            אשר ייבוא ושמור ({(dat?.funds?.length || 0) + matched.reduce((s, m) => s + m.funds.length, 0) + unmatched.reduce((s, m) => s + m.funds.length, 0)} פוליסות)
          </button>
        </div>
      </div>
    </div>
  );
}
